import { SettingsType } from '../../../shared/api/pipeex/pcfSettingsClient';
import { postHostMessage } from '../../../shared/lib/hostRibbon/hostRibbonTransport';
import { useRevitParametersStore } from '../../../shared/stores/useRevitParametersStore';
import { loadProfileSettings } from './project-gate/profileSettingsInspection';
import {
  buildProfileContents,
  getProfileParameterKindLabel,
  type ProfileParameterKind,
} from './profileContents';
import {
  buildSettingInfoProfileKey,
  getProfileKeyParts,
} from './mappingPageProjectProfileUtils';
import { toTraceKey, traceMappingFlow } from './mappingTrace';
import type { MappingHostRibbonProfile } from './mappingHostRibbonCommands';

export interface ProfileDetailsGroupMessage {
  kind: ProfileParameterKind;
  label: string;
  parameters: string[];
}

export interface ProfileDetailsMessage {
  profileKey: string;
  profileName: string;
  scopeLabel: string;
  error: string | null;
  groups: ProfileDetailsGroupMessage[];
}

const getScopeLabel = (settingsType: SettingsType): string =>
  settingsType === SettingsType.Company ? 'Company' : 'User';

const postProfileDetails = (message: ProfileDetailsMessage): void => {
  postHostMessage('profileDetails', message as unknown as Record<string, unknown>);
};

/**
 * Answers the native ribbon's "show profile details" request: resolves the
 * profile's parameter contents (through the warm inspection cache when the
 * gate already inspected it) and posts one profileDetails message the WPF
 * shell renders in its own window. The shell owns no auth token, so the data
 * must come from this side of the bridge.
 */
export async function publishProfileDetails(
  profileKey: string,
  profiles: MappingHostRibbonProfile[]
): Promise<void> {
  const keyParts = getProfileKeyParts(profileKey);
  const profileName = keyParts.name;
  const scopeLabel = getScopeLabel(keyParts.settingsType);
  const profile = profiles.find(
    (candidate) => buildSettingInfoProfileKey(candidate) === profileKey
  );

  if (!profile) {
    postProfileDetails({
      profileKey,
      profileName,
      scopeLabel,
      error: 'This profile is no longer available.',
      groups: [],
    });
    return;
  }

  const result = await loadProfileSettings({
    name: profile.name,
    settingsType: keyParts.settingsType,
    createdOn: '',
    changedOn: '',
  });

  if (!result.dto) {
    traceMappingFlow('profile details publish failed', {
      profileKey: toTraceKey(profileKey),
      error: result.error,
    });
    postProfileDetails({
      profileKey,
      profileName,
      scopeLabel,
      error: result.error ?? 'Profile contents could not be loaded.',
      groups: [],
    });
    return;
  }

  const catalogState = useRevitParametersStore.getState();
  const contents = buildProfileContents(result.dto, catalogState.parameters);
  traceMappingFlow('profile details published', {
    profileKey: toTraceKey(profileKey),
    totalCount: contents.totalCount,
    groupCount: contents.groups.length,
  });
  postProfileDetails({
    profileKey,
    profileName,
    scopeLabel,
    error: null,
    groups: contents.groups.map((group) => ({
      kind: group.kind,
      label: getProfileParameterKindLabel(group.kind),
      parameters: group.entries.map((entry) => entry.name),
    })),
  });
}
